/**
 * AddImmunizationEditor — inline Add/Edit form for the Immunizations
 * chart tab (PROD-827 chart write surfaces).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/vax_tab.dart` ("Vax" is
 * Flutter's shorthand; we use the longer-form "Immunizations" label).
 *
 * **Two modes, same form:**
 * - Add (`editing` omitted) → POST `/CreateImmunization` with
 *   `CreateImmunizationBody`. `performer` = current user GUID,
 *   `performerDetails: null`, `createdDate: null` — server fills.
 * - Edit (`editing` is a `PatientImmunization`) → PUT
 *   `/immunization/{id}` with `UpdateImmunizationBody`. `performer`
 *   preserved from the ORIGINAL record — refusing to ship if missing
 *   (strict audit guard, same as Allergies/Procedures recorder
 *   guard). `performerDetails` + `createdDate` round-tripped.
 *
 * **Immunization-specific wire quirks** (curl-verified 2026-05-20):
 * - `vaccine`, `site`, `durationQuantity`, `durationUnits` all carry
 *   the 6-field `ImmunizationCoding` shape with PascalCase
 *   `Definition` — picker rows ship VERBATIM (no field stripping).
 * - `administratedDate` / `expiryDate` use the dynamic-date wire pair
 *   (ISO no-Z + DD/MM/YYYY display). Single `DynamicDateInput` per
 *   field — matches the Procedures/Social History pattern.
 * - `quantity` ships as the all-null `DoseQuantity` wrapper, never
 *   omitted. (Flutter's `Quantity` class — dose tracking is in
 *   `durationQuantity`/`durationUnits`, not in `quantity`.)
 * - `dataAbsentReason: "n/a"` + `statusReason: "n/a"` literal
 *   sentinels (PROD-842). `remarks: ""` empty string default.
 * - `patient` field (NOT `subject` like every other chart resource).
 * - `isRx: false` for record-only entries — the chart tab is the
 *   non-Rx surface. Rx-tracked vaccines come via the prescription
 *   flow and ship `true`; we never set `true` here.
 */

import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button, FieldLabel, Select } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { TextArea as Textarea } from '@scripsteam/scrips-signal-ds';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useAuthStore } from '@/auth/auth-store';
import { useVaccineCodeSearch } from '@/patients/services/use-vaccine-code-search';
import {
  useAdministrationSiteList,
  useVaccinationDurationQuantity,
  useVaccinationDurationUnits,
} from '@/patients/services/use-immunization-master-lookups';
import { useCreateImmunization } from '@/patients/services/use-create-immunization';
import { useUpdateImmunization } from '@/patients/services/use-update-immunization';
import { normalizePerformerDetails } from './add-allergy-dialog';
import {
  DATA_ABSENT_REASON_SENTINEL,
  STATUS_REASON_SENTINEL,
} from '@/patients/lib/chart-wire-literals';
import {
  readStringField,
  unwrapMasterRow,
} from '@/patients/lib/unwrap-master-row';
import { DynamicDateInput } from './dynamic-date-input';
import type {
  CreateImmunizationBody,
  ImmunizationCoding,
  PatientImmunization,
  UpdateImmunizationBody,
} from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';

// ──────────────────────────────────────────────────────────────────────────
// Zod schema
// ──────────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    vaccineJson: z.string().min(1, 'Pick a vaccine'),
    administratedDisplay: z
      .string()
      .min(1, 'Administered date is required')
      .max(50),
    administratedIso: z.string().nullable(),
    expiryDisplay: z.string().max(50).optional(),
    expiryIso: z.string().nullable().optional(),
    lotNo: z.string().max(50).optional(),
    siteCode: z.string().optional(),
    durationQtyCode: z.string().optional(),
    durationUnitsCode: z.string().optional(),
    remarks: z.string().max(500).optional(),
  })
  .refine((v) => v.administratedIso !== null, {
    message:
      'Administered date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
    path: ['administratedDisplay'],
  })
  .refine(
    (v) => {
      if (!v.expiryDisplay?.trim()) return true;
      return v.expiryIso != null;
    },
    {
      message:
        'Expiry date must be a parseable date (DD/MM/YYYY, MM/YYYY, YYYY, or 1Y/2M/3D)',
      path: ['expiryDisplay'],
    },
  );

type FormValues = z.infer<typeof schema>;

// ──────────────────────────────────────────────────────────────────────────
// Editor
// ──────────────────────────────────────────────────────────────────────────

export interface AddImmunizationEditorProps {
  patientId: string;
  onClose: () => void;
  editing?: PatientImmunization | null;
}

export function AddImmunizationEditor({
  patientId,
  onClose,
  editing = null,
}: AddImmunizationEditorProps) {
  const isEditing = !!editing?.identifier;

  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  // Small static pickers — prefetched at mount so Edit-mode form can
  // resolve the existing codes on initialValues.
  const sitesQ = useAdministrationSiteList(true);
  const durQtyQ = useVaccinationDurationQuantity(true);
  const durUnitsQ = useVaccinationDurationUnits(true);

  // Defensive normalize for all three Select-backed master lookups.
  // The Immunization master endpoints could surface the wrapped row
  // shape (PR #225/#232/#253 lineage). Mapping ONCE here means both
  // the Select render AND the submit-handler `find()` lookup see
  // consistent flat shapes. Without this, a wrapped row would make
  // `r.code === values.siteCode` always false and the wire body
  // would ship null for site/durationQty/durationUnits even when
  // the user picked them.
  const sites = useMemo(
    () => (sitesQ.data ?? []).map(toFlatCoding),
    [sitesQ.data],
  );
  const durQtys = useMemo(
    () => (durQtyQ.data ?? []).map(toFlatCoding),
    [durQtyQ.data],
  );
  const durUnits = useMemo(
    () => (durUnitsQ.data ?? []).map(toFlatCoding),
    [durUnitsQ.data],
  );

  const initialValues = useMemo<FormValues>(() => {
    if (!editing) {
      return {
        vaccineJson: '',
        administratedDisplay: '',
        administratedIso: null,
        expiryDisplay: '',
        expiryIso: null,
        lotNo: '',
        siteCode: '',
        durationQtyCode: '',
        durationUnitsCode: '',
        remarks: '',
      };
    }
    // Reconstruct vaccine picker JSON from the read-side shape.
    const vaccineRow: ImmunizationCoding | null = editing.vaccine
      ? {
          code: editing.vaccine.code ?? null,
          Definition: null,
          display: editing.vaccine.display ?? null,
          system: editing.vaccine.system ?? null,
          description: editing.vaccine.description ?? null,
          shortName: editing.vaccine.shortName ?? null,
        }
      : null;
    return {
      vaccineJson: vaccineRow ? JSON.stringify(vaccineRow) : '',
      administratedDisplay: editing.administratedDisplayDate ?? '',
      administratedIso: editing.administratedDate ?? null,
      expiryDisplay: editing.expiryDisplayDate ?? '',
      expiryIso: editing.expiryDate ?? null,
      lotNo: editing.lotNo ?? '',
      siteCode: editing.site?.code ?? '',
      durationQtyCode: editing.durationQuantity?.code ?? '',
      durationUnitsCode: editing.durationUnits?.code ?? '',
      remarks: editing.remarks ?? '',
    };
  }, [editing]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues,
  });

  const createMut = useCreateImmunization();
  const updateMut = useUpdateImmunization();
  const isPending = createMut.isPending || updateMut.isPending;

  const submit = form.handleSubmit(async (values) => {
    if (!user) {
      toast.error('Your session has expired. Please sign in again.');
      return;
    }
    if (!userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }
    const vaccine = safeParse<ImmunizationCoding>(values.vaccineJson);
    if (!vaccine) {
      toast.error('Form values are corrupted — please re-pick the vaccine.');
      return;
    }
    // Picker lookups — match by code against the NORMALIZED arrays
    // (sites / durQtys / durUnits — see useMemo above). Optional
    // pickers ship null when unset.
    const site = sites.find((r) => r.code === values.siteCode) ?? null;
    const durationQty =
      durQtys.find((r) => r.code === values.durationQtyCode) ?? null;
    const durationUnits =
      durUnits.find((r) => r.code === values.durationUnitsCode) ?? null;

    try {
      if (isEditing && editing) {
        // Strict audit guard — Immunization's `performer` is the
        // ORIGINAL recording user (audit trail). Refuse to ship
        // null rather than substituting the editing user. Same
        // pattern as Allergies `recorder` + Procedures `asserter`.
        //
        // **UX-visible failure mode** (same as Flags PR #250 §2):
        // toast.error fires via sonner, mutation NOT called,
        // editor stays open with user input intact. Storybook
        // story `EditModeMissingRecorder` demonstrates this.
        if (!editing.performer) {
          toast.error(
            'Cannot edit — this record is missing its original recorder. Please contact support.',
          );
          return;
        }
        const body: UpdateImmunizationBody = {
          identifier: editing.identifier!,
          vaccine: normalizeCoding(vaccine),
          status: 'Active',
          patient: patientId,
          encounter: editing.encounter ?? null,
          dataAbsentReason: DATA_ABSENT_REASON_SENTINEL,
          // Original recorder preserved — NOT overwritten with userId.
          performer: editing.performer,
          expiryDisplayDate: values.expiryDisplay?.trim()
            ? values.expiryDisplay
            : null,
          administratedDisplayDate: values.administratedDisplay,
          administratedDate: values.administratedIso,
          expiryDate: values.expiryIso ?? null,
          quantity: ALL_NULL_QUANTITY,
          statusReason: STATUS_REASON_SENTINEL,
          site: site ? normalizeCoding(site) : null,
          remarks: values.remarks?.trim() ?? '',
          lotNo: values.lotNo?.trim() || null,
          isDeleted: false,
          performerDetails: normalizePerformerDetails(editing.performerDetails),
          createdDate: editing.createdDate ?? null,
          isRx: editing.isRx === true,
          durationQuantity: durationQty ? normalizeCoding(durationQty) : null,
          durationUnits: durationUnits ? normalizeCoding(durationUnits) : null,
          speciality: null,
        };
        await updateMut.mutateAsync({
          patientId,
          immunizationId: editing.identifier!,
          body,
        });
        toast.success(
          `Updated immunization: ${(vaccine.display ?? vaccine.code ?? 'Unknown').trim()}`,
        );
      } else {
        const body: CreateImmunizationBody = {
          identifier: null,
          vaccine: normalizeCoding(vaccine),
          status: 'Active',
          patient: patientId,
          encounter: null,
          dataAbsentReason: DATA_ABSENT_REASON_SENTINEL,
          // Current user on Create.
          performer: userId,
          expiryDisplayDate: values.expiryDisplay?.trim()
            ? values.expiryDisplay
            : null,
          administratedDisplayDate: values.administratedDisplay,
          administratedDate: values.administratedIso,
          expiryDate: values.expiryIso ?? null,
          quantity: ALL_NULL_QUANTITY,
          statusReason: STATUS_REASON_SENTINEL,
          site: site ? normalizeCoding(site) : null,
          remarks: values.remarks?.trim() ?? '',
          lotNo: values.lotNo?.trim() || null,
          isDeleted: false,
          performerDetails: null,
          createdDate: null,
          // Chart-side adds are record-only (non-Rx). Rx-tracked
          // vaccines come through the prescription flow.
          isRx: false,
          durationQuantity: durationQty ? normalizeCoding(durationQty) : null,
          durationUnits: durationUnits ? normalizeCoding(durationUnits) : null,
          speciality: null,
        };
        await createMut.mutateAsync({ patientId, body });
        toast.success(
          `Added immunization: ${(vaccine.display ?? vaccine.code ?? 'Unknown').trim()}`,
        );
      }
      form.reset();
      onClose();
    } catch {
      /* Global toast surfaces failure; keep editor open. */
    }
  });

  return (
    // The design's PaEditorCard: the one card (hairline, 12 corners, white, no shadow, 24 padding); look in
    // showcase-patient.css `editor card`.
    <div data-slot="editor-card">
      <form onSubmit={submit} className="space-y-6">
        <Controller
          name="vaccineJson"
          control={form.control}
          render={({ field, fieldState }) => (
            <VaccinePicker
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Controller
            name="administratedIso"
            control={form.control}
            render={({ field: isoField }) => (
              <Controller
                name="administratedDisplay"
                control={form.control}
                render={({ field: displayField, fieldState }) => (
                  <DynamicDateInput
                    label="Administered date"
                    required
                    displayValue={displayField.value}
                    isoValue={isoField.value}
                    onChange={(next) => {
                      displayField.onChange(next.displayValue);
                      isoField.onChange(next.isoValue);
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
          <Controller
            name="expiryIso"
            control={form.control}
            render={({ field: isoField }) => (
              <Controller
                name="expiryDisplay"
                control={form.control}
                render={({ field: displayField, fieldState }) => (
                  <DynamicDateInput
                    label="Expiry date"
                    // Expiry can be future — override the max bound. The year
                    // dropdown comes with it: arrows alone cannot reach 2030.
                    max={new Date(2100, 0, 1)}
                    captionLayout="dropdown"
                    displayValue={displayField.value ?? ''}
                    isoValue={isoField.value ?? null}
                    onChange={(next) => {
                      displayField.onChange(next.displayValue);
                      isoField.onChange(next.isoValue);
                    }}
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <FieldLabel
              htmlFor="lotNo"
              
            >
              Lot no.
            </FieldLabel>
            <TextField
              id="lotNo"
              {...form.register('lotNo')}
              placeholder="Lot / batch number"
              maxLength={50}
              className="mt-1"
            />
          </div>
          <div>
            <FieldLabel
              htmlFor="site"
              
            >
              Site
            </FieldLabel>
            <Controller
              name="siteCode"
              control={form.control}
              render={({ field }) => (
                <Select
                  id="site"
                  className="mt-1"
                  value={field.value || undefined}
                  onChange={field.onChange}
                  disabled={sitesQ.isLoading}
                  placeholder={sitesQ.isLoading ? 'Loading…' : 'Pick a site'}
                  options={sites.map((row) => ({
                    label: row.display ?? row.code ?? '—',
                    value: row.code ?? '',
                  }))}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <FieldLabel
                htmlFor="durationQty"
                
              >
                Dose #
              </FieldLabel>
              <Controller
                name="durationQtyCode"
                control={form.control}
                render={({ field }) => (
                  <Select
                    id="durationQty"
                    className="mt-1"
                    value={field.value || undefined}
                    onChange={field.onChange}
                    disabled={durQtyQ.isLoading}
                    placeholder="—"
                    options={durQtys.map((row) => ({
                      label: row.display ?? row.code ?? '—',
                      value: row.code ?? '',
                    }))}
                  />
                )}
              />
            </div>
            <div>
              <FieldLabel
                htmlFor="durationUnits"
                
              >
                Unit
              </FieldLabel>
              <Controller
                name="durationUnitsCode"
                control={form.control}
                render={({ field }) => (
                  <Select
                    id="durationUnits"
                    className="mt-1"
                    value={field.value || undefined}
                    onChange={field.onChange}
                    disabled={durUnitsQ.isLoading}
                    placeholder="—"
                    options={durUnits.map((row) => ({
                      label: row.display ?? row.code ?? '—',
                      value: row.code ?? '',
                    }))}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div>
          <FieldLabel
            htmlFor="remarks"
            
          >
            Remarks
          </FieldLabel>
          <Textarea
            id="remarks"
            {...form.register('remarks')}
            placeholder="Optional context, e.g. reaction notes…"
            rows={3}
            maxLength={500}
            className="mt-1"
          />
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Button type="submit" disabled={isPending}>
            {isPending ? (
              <>
                Saving…
              </>
            ) : isEditing ? (
              'Update'
            ) : (
              'Done'
            )}
          </Button>
          <Button
            type="button"
            variant="tertiary"
            onClick={() => {
              form.reset();
              onClose();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// VaccinePicker — CVX typeahead
// ──────────────────────────────────────────────────────────────────────────

function VaccinePicker({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (next: string) => void;
  error?: string;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 250);
  const searchQ = useVaccineCodeSearch(debounced, true);

  const selected = useMemo<ImmunizationCoding | null>(
    () => (value ? safeParse<ImmunizationCoding>(value) : null),
    [value],
  );

  const results = searchQ.data ?? [];

  return (
    <div>
      <FieldLabel
        htmlFor="vaccine"
        
      >
        Vaccine <span className="text-red-700">*</span>
      </FieldLabel>

      {selected ? (
        (() => {
          // Defensive re-normalize in case a legacy stored value still
          // carries the wrapped master-row shape — `normalizeCoding`
          // unwraps `{code: Coding}` and guarantees string fields.
          const flat = normalizeCoding(selected);
          const codeStr = flat?.code ?? '';
          const nameStr = (flat?.display ?? '').trim();
          return (
            <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-border-default bg-blue-100/30 px-3 py-2">
              <div className="min-w-0">
                <p className={v2Look('text-sm font-medium text-brand-depth truncate', V2_BODY_EMPHASIS)}>
                  {nameStr || codeStr || '—'}
                </p>
                {codeStr ? (
                  <p className="text-xs text-text-hint">
                    CVX {codeStr}
                    {flat?.description ? ` · ${flat.description}` : ''}
                  </p>
                ) : null}
              </div>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => {
                  onChange('');
                  setQuery('');
                }}
              >
                Change
              </Button>
            </div>
          );
        })()
      ) : (
        <>
          <div className="relative mt-1">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              id="vaccine"
              type="text"
              placeholder="Type to search (e.g. covid, moderna, 91301)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10"
              autoComplete="off"
            />
          </div>

          {debounced.trim().length >= 1 ? (
            <div className="mt-1 max-h-56 overflow-y-auto scrollbar-hide rounded-md border border-border-default bg-surface-card divide-y divide-border-default">
              {searchQ.isFetching ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="px-3 py-2 text-sm text-text-muted">
                  No matches. Try another word or a code.
                </div>
              ) : (
                results.map((row, idx) => {
                  const flat = normalizeCoding(row);
                  if (!flat) return null;
                  const codeStr = flat.code ?? '';
                  const nameStr = (flat.display ?? '').trim();
                  // Key always includes idx so duplicate-key warnings
                  // can't trigger even if the master endpoint ever
                  // returns rows with the same code+display pair.
                  const rowKey = `${codeStr}-${nameStr}-${idx}`;
                  return (
                    <button
                      key={rowKey}
                      type="button"
                      onClick={() => {
                        onChange(JSON.stringify(flat));
                        setQuery('');
                      }}
                      className="block w-full px-3 py-2 text-left hover:bg-grey-100/50"
                    >
                      <span className="text-sm text-text-primary block">
                        {nameStr || codeStr || '—'}
                      </span>
                      {codeStr ? (
                        <span className="text-xs text-text-hint">
                          CVX {codeStr}
                        </span>
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p className="text-xs text-red-700 mt-1" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function safeParse<T>(s: string): T | null {
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/** All-null `DoseQuantity` wrapper — Flutter `Quantity.toJson` default
 *  when null. Backend rejects missing key, accepts the all-null wrap. */
const ALL_NULL_QUANTITY = Object.freeze({
  value: null,
  unit: null,
  system: null,
  code: null,
}) as { value: null; unit: null; system: null; code: null };

/** Map a master-endpoint row to the flat `ImmunizationCoding` shape,
 *  defensively unwrapping the `{code: ActualCoding}` wrapping that
 *  some master endpoints surface (PR #225/#232/#253 lineage). Used
 *  inside `useMemo` at the top of the editor so Select renders + the
 *  submit-handler `find()` lookups see consistent shapes. */
function toFlatCoding(row: unknown): ImmunizationCoding {
  const inner = unwrapMasterRow(row);
  return {
    code: readStringField(inner, 'code'),
    Definition: (inner?.Definition as string | null) ?? null,
    display: readStringField(inner, 'display'),
    system: readStringField(inner, 'system'),
    description: readStringField(inner, 'description'),
    shortName: readStringField(inner, 'shortName'),
  };
}

/**
 * Defensive normalisation for `ImmunizationCoding` rows.
 *
 * **Two responsibilities:**
 *
 * 1. **Unwrap master-endpoint row wrapping**. Some Scrips Master
 *    endpoints return rows shaped `{code: ActualCoding, ...}` — the
 *    actual Coding is nested under a `code` key. Andrew browser-test
 *    2026-05-20 caught this on `VaccinationCodesList`: the picker
 *    crashed `flat.display.trim()` because `flat.display` was the
 *    inner Coding object, not a string. Same bug pattern as
 *    DiagnosisCodes (PR #225, fix 9295a3f) and ProcedureCodesList
 *    (PR #232, fix 196d780). Detect by checking if the `code` field
 *    is itself an object — if so, unwrap.
 *
 * 2. **Ensure every one of the 6 wire fields is present** (with
 *    `null` for any missing field) so the backend doesn't reject a
 *    partial Coding. PascalCase `Definition` preserved verbatim
 *    (case-sensitive wire literal — PROD-842).
 */
function normalizeCoding(
  row:
    | ImmunizationCoding
    | { code?: ImmunizationCoding | string | null; [k: string]: unknown }
    | null
    | undefined,
): ImmunizationCoding | null {
  if (!row) return null;
  // Defensive unwrap: if `code` is itself an object, the row is wrapped.
  // The actual Coding lives one level deeper.
  const codeField = (row as { code?: unknown }).code;
  if (codeField !== null && typeof codeField === 'object') {
    const inner = codeField as ImmunizationCoding;
    return {
      code: typeof inner.code === 'string' ? inner.code : null,
      Definition: inner.Definition ?? null,
      display: typeof inner.display === 'string' ? inner.display : null,
      system: typeof inner.system === 'string' ? inner.system : null,
      description:
        typeof inner.description === 'string' ? inner.description : null,
      shortName:
        typeof inner.shortName === 'string' ? inner.shortName : null,
    };
  }
  // Flat shape — the common case for Site / DurationQty / DurationUnits.
  const flat = row as ImmunizationCoding;
  return {
    code: typeof flat.code === 'string' ? flat.code : null,
    Definition: flat.Definition ?? null,
    display: typeof flat.display === 'string' ? flat.display : null,
    system: typeof flat.system === 'string' ? flat.system : null,
    description:
      typeof flat.description === 'string' ? flat.description : null,
    shortName: typeof flat.shortName === 'string' ? flat.shortName : null,
  };
}
